import { MmuTranslateFileLoaderBase } from './mmu-translate-file-loader-base';
import { TranslateLoader } from '@ngx-translate/core';
import { from, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';


/**
 * Function that imports the language file for the given locale. It should return a Promise that resolves to the translations object.
 * Typically, this should be a dynamic import statement that loads the JSON file for the given locale.
 */
export type ImportLanguageFn = (locale: string) => Promise<any>;

class NamespacedTranslateLoader extends MmuTranslateFileLoaderBase {
  constructor(
    protected readonly namespace: string,
    protected readonly importLanguageFn: ImportLanguageFn
  ) {
    super();
  }

  protected loadLanguageForLocale(locale: string): Observable<unknown> {
    const translationPromise = this.importLanguageFn(locale);

    return from(translationPromise).pipe(
      catchError((e) => {
        // In case something unexpected happens, we display a warning and return an empty object to prevent the app from crashing
        console.warn(`[AppTranslateLoader] Failed to load translations for locale ${locale} in namespace ${this.namespace}`, e);
        return of({});
      })
    );
  }
}

/**
 * Creates a namespaced instance of the MmuTranslateFileLoader. Internally, a temporary class is created that extends the
 * MmuTranslateFileLoaderBase and uses the namespace provided.
 *
 * See the example below for how to use this function.
 *
 * @example
 * ```typescript
 * TranslateModule.forChild({
 *   //...
 *   loader: { provide: TranslateLoader, useFactory: createNamespacedTranslateLoader('myNamespace', (locale: string) => {
 *     return import(
 *       /\* webpackInclude: /\\.json$/ \*\/
 *       /\* webpackChunkName: "myNamespace-[request]" \*\/
 *       './${locale}.json'
 *     )}
 *   )}
 * })
 * ```
 * @see createNamespacedTranslateParser
 * @param {string} namespace The namespace in which the translations should be wrapped
 * @param {(locale: string) => Observable<any>} importLanguageFn The function that imports the language file for the given locale. It should
 * return a Promise that resolves to the translations object for the given locale. It must be provided locally in each vertical because
 * the dynamic import must be handled by Webpack.
 * @returns {MmuTranslateFileLoaderBase} The namespaced instance of the MmuTranslateFileLoader
 */
export function createNamespacedTranslateLoader(namespace: string, importLanguageFn: ImportLanguageFn): TranslateLoader {
  return new NamespacedTranslateLoader(namespace, importLanguageFn);
}
